import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, X, Images, Sparkles, Loader2, Search } from "lucide-react";
import ContentProtection from "../components/ContentProtection";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authToken = () => {
  try {
    return JSON.parse(localStorage.getItem("ranker-auth"))?.token || "";
  } catch {
    return "";
  }
};

const imgSrc = (item) =>
  `${process.env.REACT_APP_BACKEND_URL}${item.img_url}?auth=${encodeURIComponent(authToken())}`;

const Card = ({ item, onOpen }) => {
  const locked = item.locked;
  return (
    <button
      onClick={() => onOpen(item)}
      data-testid={`vr-card-${item.id}`}
      className="group text-left rounded-xl border border-stone-200 bg-white overflow-hidden hover:border-stone-300 hover:shadow-md transition-all duration-150"
    >
      <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
        <img
          src={locked ? `${imgSrc(item)}&preview=1` : imgSrc(item)}
          alt={item.title}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-200 ${
            locked ? "select-none" : "group-hover:scale-[1.03]"
          }`}
        />
        {locked && <div className="absolute inset-0 bg-stone-900/5" />}

        {/* category + type badges (top-left) */}
        <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-stone-900/85 text-white backdrop-blur-sm">
            {item.category}
          </span>
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-orange-500 text-white">
            Diagram
          </span>
        </div>

        {/* PRO lock badge (top-right) */}
        {item.is_pro && (
          <span
            className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              locked ? "bg-orange-500 text-white" : "bg-emerald-500 text-white"
            }`}
          >
            <Lock className="w-3 h-3" /> PRO
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-semibold text-stone-800 leading-snug line-clamp-2">
          {item.title}
        </p>
      </div>
    </button>
  );
};

export default function VisualResourcePage() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE}/visual-resources/categories`)
      .then((r) => {
        setCats(r.data.categories || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE}/visual-resources`, { params: active === "all" ? {} : { category: active } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [active]);

  const tabs = useMemo(
    () => [{ slug: "all", category: "All", count: total }, ...cats],
    [cats, total]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.title.toLowerCase().includes(q) ||
        it.category.toLowerCase().includes(q)
    );
  }, [items, query]);

  const openItem = (item) => {
    if (item.locked) {
      navigate("/upgrade");
      return;
    }
    setSelected(item);
  };

  return (
    <ContentProtection>
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="visual-resource-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center gap-2 text-orange-500">
            <Images className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wide">Visual Resource</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-stone-900 tracking-tight mt-1">
            Diagram Library
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            High-yield visual diagrams for faster revision. First few are free — unlock the full library with PRO.
          </p>
          {/* Search */}
          <div className="relative mt-4 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search diagrams by keyword…"
              data-testid="vr-search-input"
              className="w-full h-10 pl-9 pr-9 rounded-xl bg-stone-50 focus:bg-white border border-stone-200 hover:border-stone-300 focus:border-orange-500 outline-none focus:ring-2 focus:ring-orange-500/20 text-sm text-stone-900 placeholder:text-stone-400 transition-all duration-150"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="vr-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* Category filter */}
        {tabs.length > 1 && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {tabs.map((t) => (
                <button
                  key={t.slug}
                  onClick={() => setActive(t.slug)}
                  data-testid={`vr-tab-${t.slug}`}
                  className={`h-8 px-3 inline-flex items-center gap-1.5 rounded-xl text-xs font-bold border-2 border-b-[3px] transition-all duration-150 whitespace-nowrap ${
                    active === t.slug
                      ? "bg-orange-50 text-orange-700 border-orange-200 border-b-orange-500"
                      : "bg-transparent text-stone-600 border-transparent hover:bg-stone-100 hover:text-stone-900 hover:border-stone-200"
                  }`}
                >
                  {t.category}
                  <span className="text-[10px] font-semibold text-stone-400">{t.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-stone-400" data-testid="vr-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="vr-empty">
            <Sparkles className="w-10 h-10 text-orange-300 mb-3" />
            <h2 className="text-lg font-bold text-stone-800 font-heading">
              {query ? "No diagrams match your search" : "No diagrams yet"}
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              {query ? `Nothing found for “${query}”. Try another keyword.` : "The visual library will appear here soon."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {visible.map((it) => (
              <Card key={it.id} item={it} onOpen={openItem} />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-stone-900/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="vr-lightbox"
        >
          <div
            className="relative max-w-5xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wide text-orange-500">
                  {selected.category}
                </p>
                <h3 className="text-sm font-bold text-stone-900 truncate">{selected.title}</h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="h-8 w-8 grid place-items-center rounded-lg hover:bg-stone-100 text-stone-500"
                data-testid="vr-lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-stone-50 max-h-[80vh] overflow-auto">
              <img src={imgSrc(selected)} alt={selected.title} className="w-full h-auto" />
            </div>
          </div>
        </div>
      )}
    </div>
    </ContentProtection>
  );
}
